import unauthAxios, { unauthAxiosFetcher } from "../axios";
import authAxios, { authAxiosFetcher } from "../authAxios";
import useSWR from "swr";

const MAX_MEDIA_UPLOAD_BYTES = 300 * 1024 * 1024;
const MAX_IMAGE_UPLOAD_BYTES = 8 * 1024 * 1024;
const MAX_PASSPORT_UPLOAD_BYTES = 8 * 1024 * 1024;

function resolveMime(file) {
  if (file.type) return file.type;
  const name = (file.name || "").toLowerCase();
  if (name.endsWith(".pdf")) return "application/pdf";
  if (name.endsWith(".png")) return "image/png";
  if (name.endsWith(".heic")) return "image/heic";
  if (name.endsWith(".webp")) return "image/webp";
  if (name.endsWith(".jpg") || name.endsWith(".jpeg")) return "image/jpeg";
  return "application/octet-stream";
}

export class UploadError extends Error {
  constructor(message, { status, cause } = {}) {
    super(message);
    this.name = "UploadError";
    this.status = status;
    this.cause = cause;
  }
}

export const uploadBlobToS3 = async (blob) => {
  let formData = new FormData();
  formData.append("media", blob);

  const result = await unauthAxios.post("/v1/upload/aws", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });

  return result.data.url;
};

export const uploadBigFileToS3 = async (file) => {
  if (!file) throw new UploadError("No file selected.");

  const mime = resolveMime(file);
  const isVideo = mime.startsWith("video/");
  const maxUploadBytes = isVideo ? MAX_MEDIA_UPLOAD_BYTES : MAX_IMAGE_UPLOAD_BYTES;
  const maxUploadMegabytes = maxUploadBytes / (1024 * 1024);
  if (file.size > maxUploadBytes) {
    throw new UploadError(
      `File is ${(file.size / (1024 * 1024)).toFixed(1)} MB — please use one under ${maxUploadMegabytes} MB.`
    );
  }

  const safeName = (file.name || "upload").replace(/\s+/g, "_");
  const s3Key = `${Date.now()}_${safeName}`;
  const options = {
    headers: {
      "Content-Type": mime,
    },
  };

  let signedUploadUrl;
  let resourceUrl;
  try {
    const res = await unauthAxios.get("/v1/upload/getSignedUrl", {
      params: { key: s3Key, file_type: mime },
    });
    signedUploadUrl = res.data.signedUploadUrl;
    resourceUrl = res.data.resourceUrl;
  } catch (err) {
    console.error("getSignedUrl failed", err);
    if (typeof window !== "undefined" && window.Sentry?.captureException)
      window.Sentry.captureException(err, { tags: { surface: "uploadBigFileToS3.getSignedUrl" } });
    throw new UploadError(
      "Couldn't reach the upload service. Check your connection and try again.",
      {
        status: err?.response?.status,
        cause: err,
      }
    );
  }

  try {
    await unauthAxios.put(signedUploadUrl, file, options);
  } catch (err) {
    console.error("S3 PUT failed", err);
    if (typeof window !== "undefined" && window.Sentry?.captureException)
      window.Sentry.captureException(err, {
        tags: { surface: "uploadBigFileToS3.s3Put" },
        extra: { mime, size: file.size, name: file.name },
      });
    throw new UploadError(
      `Upload failed (${err?.response?.status ?? "network"}). Try a smaller file or a different format.`,
      { status: err?.response?.status, cause: err }
    );
  }

  return resourceUrl;
};

export const createVideoThumbnail = (file, { seekTo = 1, quality = 0.82, maxSide = 600 } = {}) =>
  new Promise((resolve, reject) => {
    if (!file?.type?.startsWith("video/")) {
      reject(new UploadError("A valid video file is required."));
      return;
    }

    const player = document.createElement("video");
    const objectUrl = URL.createObjectURL(file);
    const cleanup = () => {
      player.removeAttribute("src");
      player.load();
      URL.revokeObjectURL(objectUrl);
    };
    const fail = () => {
      cleanup();
      reject(new UploadError(`${file.name || "The selected file"} is not a supported video.`));
    };

    player.muted = true;
    player.playsInline = true;
    player.preload = "metadata";
    player.addEventListener("error", fail, { once: true });
    player.addEventListener(
      "loadedmetadata",
      () => {
        const target = Math.min(seekTo, Math.max(0, player.duration / 2));
        player.addEventListener(
          "seeked",
          () => {
            const largestSide = Math.max(player.videoWidth, player.videoHeight);
            if (!largestSide) {
              fail();
              return;
            }
            const scale = Math.min(1, maxSide / largestSide);
            const canvas = document.createElement("canvas");
            canvas.width = Math.round(player.videoWidth * scale);
            canvas.height = Math.round(player.videoHeight * scale);
            canvas.getContext("2d").drawImage(player, 0, 0, canvas.width, canvas.height);
            canvas.toBlob(
              (blob) => {
                cleanup();
                if (blob) resolve(blob);
                else reject(new UploadError("Couldn't create a thumbnail for this video."));
              },
              "image/jpeg",
              quality
            );
          },
          { once: true }
        );
        player.currentTime = target;
      },
      { once: true }
    );
    player.src = objectUrl;
  });

// Passport-specific upload: authenticated, targets the PRIVATE bucket, and returns
// an opaque KEY (stored as users.passport_image_key) — never a public URL. The
// passport is only ever viewed via a short-lived presigned GET (/v1/passport/getViewUrl).
export const uploadPassportToPrivateS3 = async (file) => {
  if (!file) throw new UploadError("No file selected.");

  if (file.size > MAX_PASSPORT_UPLOAD_BYTES) {
    throw new UploadError(
      `File is ${(file.size / (1024 * 1024)).toFixed(1)} MB — please use one under 8 MB.`
    );
  }

  const mime = resolveMime(file);
  const safeName = (file.name || "passport").replace(/\s+/g, "_");

  let signedUploadUrl;
  let passportKey;
  try {
    // Authenticated — the upload endpoint requires a logged-in user.
    const res = await authAxios.get("/v1/passport/getUploadUrl", {
      params: { file_name: safeName, file_type: mime },
    });
    signedUploadUrl = res.data?.data?.signedUploadUrl;
    passportKey = res.data?.data?.passport_key;
  } catch (err) {
    console.error("getUploadUrl failed", err);
    if (typeof window !== "undefined" && window.Sentry?.captureException)
      window.Sentry.captureException(err, {
        tags: { surface: "uploadPassportToPrivateS3.getUploadUrl" },
      });
    throw new UploadError(
      "Couldn't reach the upload service. Check your connection and try again.",
      { status: err?.response?.status, cause: err }
    );
  }

  if (!signedUploadUrl || !passportKey) {
    throw new UploadError("Upload service returned an invalid response.");
  }

  try {
    // Raw S3 PUT (mirrors uploadBigFileToS3 — unauthAxios so no extra params break the signature).
    await unauthAxios.put(signedUploadUrl, file, { headers: { "Content-Type": mime } });
  } catch (err) {
    console.error("Passport S3 PUT failed", err);
    if (typeof window !== "undefined" && window.Sentry?.captureException)
      window.Sentry.captureException(err, {
        tags: { surface: "uploadPassportToPrivateS3.s3Put" },
        extra: { mime, size: file.size },
      });
    throw new UploadError(
      `Upload failed (${err?.response?.status ?? "network"}). Try a smaller file or a different format.`,
      { status: err?.response?.status, cause: err }
    );
  }

  return passportKey;
};
